import { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { profileApi, profileQueryKeys, type Profile } from '@entities/profile';
import { showToast } from '@features/ToastManager';

import { PROFILE_TOAST_SCOPE } from './toast';

import type { IDataResponseError } from '@shared/model/types/dataResponse.inteface';

export const useProfilePreferences = (profile: Profile) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showHints, setShowHints] = useState(profile.preferences.needShowTooltips);

    useEffect(() => {
        setShowHints(profile.preferences.needShowTooltips);
    }, [profile.preferences.needShowTooltips]);

    const mutation = useMutation({
        mutationFn: profileApi.updatePreferences,
        onSuccess: (updatedProfile) => {
            queryClient.setQueryData(profileQueryKeys.current(), updatedProfile);
            showToast(t('profile.page.settings.hints.success'), { scope: PROFILE_TOAST_SCOPE });
        },
        onError: (error) => {
            const responseError = isAxiosError<IDataResponseError>(error)
                ? error.response?.data
                : undefined;
            setShowHints(profile.preferences.needShowTooltips);
            showToast(responseError?.message ?? t('profile.page.settings.hints.errors.save'), {
                scope: PROFILE_TOAST_SCOPE,
                type: 'error',
            });
        },
    });

    const changeShowHints = (needShowTooltips: boolean) => {
        if (needShowTooltips === showHints || mutation.isPending) {
            return;
        }
        setShowHints(needShowTooltips);
        mutation.mutate({ 'need_show_tooltips': needShowTooltips });
    };

    return { showHints, changeShowHints, isPending: mutation.isPending };
};
